import { applyHomography, computeHomography, type Homography3x3, type Point } from './Homography';

export type CalibratorState = 'idle' | 'picking' | 'ready';

export interface CourtDimensions {
  widthMeters: number;
  heightMeters: number;
}

export const BASKETBALL_COURT: CourtDimensions = {
  widthMeters: 28,
  heightMeters: 15,
};

const CORNER_LABELS = ['top-left', 'top-right', 'bottom-right', 'bottom-left'] as const;

export type CornerLabel = (typeof CORNER_LABELS)[number];

export class CourtCalibrator {
  private points: Point[] = [];
  private homography: Homography3x3 | null = null;

  constructor(public readonly court: CourtDimensions = BASKETBALL_COURT) {}

  state(): CalibratorState {
    if (this.homography) return 'ready';
    if (this.points.length > 0) return 'picking';
    return 'idle';
  }

  progress(): number {
    return this.points.length;
  }

  nextLabel(): CornerLabel | null {
    if (this.points.length >= 4) return null;
    return CORNER_LABELS[this.points.length];
  }

  getPoints(): readonly Point[] {
    return this.points;
  }

  getHomography(): Homography3x3 | null {
    return this.homography;
  }

  pushPoint(p: Point): CalibratorState {
    if (this.points.length >= 4) return this.state();
    this.points.push(p);
    if (this.points.length === 4) {
      const src = this.points as [Point, Point, Point, Point];
      const dst: [Point, Point, Point, Point] = [
        { x: 0, y: 0 },
        { x: this.court.widthMeters, y: 0 },
        { x: this.court.widthMeters, y: this.court.heightMeters },
        { x: 0, y: this.court.heightMeters },
      ];
      this.homography = computeHomography(src, dst);
    }
    return this.state();
  }

  reset(): void {
    this.points = [];
    this.homography = null;
  }

  imageToCourt(p: Point): Point | null {
    if (!this.homography) return null;
    return applyHomography(p, this.homography);
  }
}
